import { CommonResponse } from '@xver/@types/api';
import {
    InstanceConsoleResponse,
    InstanceTerminalEnableResponse,
    InstanceTerminalStatusResponse,
} from '@xver/@types/instance';
import httpClient from '@xver/libs/axios';
import type { PrivateNetworkState } from '../projects/model';
import {
    CloudPackagesResponseModel,
    InstanceActivityQueryModel,
    InstanceActivityResponseModel,
    InstanceCostResponseModel,
    InstanceResponseModel,
    InstanceTagResponseModel,
    InvokeActionModel,
    LaunchInstanceRequestModel,
    LaunchOptionResponseModel,
    ResizeInstanceRequestModel,
    UpdateInstanceRequestModel,
} from './model';

const ENDPOINTS = {
    GET_LAUNCH_OPTIONS: '/v1/launch',
    GET_CLOUD_PACKAGES: '/v1/packages/cloud-compute',
    LAUNCH: '/v1/launch',
    GET_LIST: '/v1/instances',
    GET_ACTIVITY_LOGS: (id: any) => `/v1/instances/${id}/activity`,
    GET_COST_SUMAMRY: (id: any) => `/v1/instances/${id}/cost`,
    GET_TAG_LIST: (id: any) => `/v1/instances/${id}/tags`,
    CREATE_TAG: (id: any) => `/v1/instances/${id}/tags`,
    DELETE_TAG: (id: any, tagId: any) => `/v1/instances/${id}/tags/${tagId}`,
    GET_ONE: (id: any) => `/v1/instances/${id}`,
    UPDATE: (id: any) => `/v1/instances/${id}`,
    ACTION: (id: any, action: InvokeActionModel) => `/v1/instances/${id}/${action}`,
    DELETE: (id: any) => `/v1/instances/${id}`,
    CONSOLE: (id: any) => `/v1/instances/${id}/console`,
    TERMINAL: (id: any) => `/v1/instances/${id}/terminal`,
    TERMINAL_ENABLE: (id: any) => `/v1/instances/${id}/terminal/enable`,
    RESIZE_OPTIONS: (id: any) => `/v1/instances/${id}/resize/options`,
    RESIZE: (id: any) => `/v1/instances/${id}/resize`,
    REAPPLY_PRIVATE_NETWORK: (id: any) => `/v1/instances/${id}/private-network/reapply`,
};

export function getLaunchOptions(): Promise<LaunchOptionResponseModel> {
    return httpClient.get(ENDPOINTS.GET_LAUNCH_OPTIONS);
}

export function getCloudComputePackages(): Promise<CloudPackagesResponseModel> {
    return httpClient.get(ENDPOINTS.GET_CLOUD_PACKAGES);
}

export function launchInstance(data: LaunchInstanceRequestModel): Promise<CommonResponse> {
    return httpClient.post(ENDPOINTS.LAUNCH, data);
}

export function getInstances(): Promise<InstanceResponseModel[]> {
    return httpClient.get(ENDPOINTS.GET_LIST);
}

export function getInstanceActivityLogs(
    id: any,
    params?: InstanceActivityQueryModel
): Promise<InstanceActivityResponseModel> {
    return httpClient.get(ENDPOINTS.GET_ACTIVITY_LOGS(id), { params });
}

export function getInstanceCostSummary(id: any): Promise<InstanceCostResponseModel> {
    return httpClient.get(ENDPOINTS.GET_COST_SUMAMRY(id));
}

export function getInstance(id: any): Promise<InstanceResponseModel> {
    return httpClient.get(ENDPOINTS.GET_ONE(id));
}

export function updateInstance(id: any, data: UpdateInstanceRequestModel): Promise<CommonResponse> {
    return httpClient.post(ENDPOINTS.UPDATE(id), data);
}

export function invokeAction(id: any, action: InvokeActionModel): Promise<CommonResponse> {
    return httpClient.post(ENDPOINTS.ACTION(id, action));
}

export function deleteInstance(id: any): Promise<CommonResponse> {
    return httpClient.delete(ENDPOINTS.DELETE(id));
}

export function getInstanceTags(id: any): Promise<InstanceTagResponseModel[]> {
    return httpClient.get(ENDPOINTS.GET_TAG_LIST(id));
}

export function createInstanceTag(id: any, tag: string): Promise<CommonResponse> {
    return httpClient.post(ENDPOINTS.CREATE_TAG(id), { tag });
}

export function deleteInstanceTag(id: any, tagId: any): Promise<CommonResponse> {
    return httpClient.delete(ENDPOINTS.DELETE_TAG(id, tagId));
}

export function openConsole(id: any): Promise<InstanceConsoleResponse> {
    return httpClient.post(ENDPOINTS.CONSOLE(id));
}

export function getTerminalStatus(id: any): Promise<InstanceTerminalStatusResponse> {
    return httpClient.get(ENDPOINTS.TERMINAL(id));
}

export function enableTerminal(id: any): Promise<InstanceTerminalEnableResponse> {
    return httpClient.post(ENDPOINTS.TERMINAL_ENABLE(id));
}

export function getResizeOptions(id: any): Promise<CloudPackagesResponseModel> {
    return httpClient.get(ENDPOINTS.RESIZE_OPTIONS(id));
}

export function resizeInstance(id: any, data: ResizeInstanceRequestModel): Promise<CommonResponse> {
    return httpClient.post(ENDPOINTS.RESIZE(id), data);
}

export function reapplyPrivateNetwork(
    id: any
): Promise<{ privateNetworkState: Extract<PrivateNetworkState, 'pending'> }> {
    return httpClient.post(ENDPOINTS.REAPPLY_PRIVATE_NETWORK(id));
}
